import { DependencyList, useEffect, useRef } from 'react';
import { Observable, OperatorFunction, ReplaySubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { depsAreSame, useCreation } from '../hooks/useCreation';

const initialInputs = Symbol('initialInputs');

export function useCreateObservable<TOutput, TInputs extends Readonly<any[]>>(
  factory: (inputs$: Observable<[...TInputs]>) => Observable<TOutput>,
  deps: DependencyList,
  inputs: [...TInputs],
): Observable<TOutput>;
export function useCreateObservable<TOutput, TInputs>(
  factory: (inputs$: Observable<TInputs>) => Observable<TOutput>,
  deps: DependencyList,
  inputs: TInputs,
): Observable<TOutput>;
export function useCreateObservable<TOutput, TInputs>(
  factory: (inputs$: Observable<TInputs>) => Observable<TOutput>,
  deps: DependencyList,
  inputs: TInputs,
): Observable<TOutput> {
  const inputs$ = useCreation(
    () => new ReplaySubject<TInputs>(1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const output$ = useCreation(
    () => factory(inputs$),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );

  const prevInputs = useRef<typeof initialInputs | TInputs>(initialInputs);
  useEffect(() => {
    let inputsAreSame = false;

    if (Array.isArray(inputs) && Array.isArray(prevInputs.current)) {
      if (inputs.length === prevInputs.current.length) {
        if (depsAreSame(inputs, prevInputs.current)) {
          inputsAreSame = true;
        }
      }
    } else {
      if (Object.is(prevInputs.current, inputs)) {
        inputsAreSame = true;
      }
    }

    prevInputs.current = inputs;
    if (!inputsAreSame) {
      inputs$.next(inputs);
    }
  });

  return output$;
}

export const distinctUntilInputsShallowChanged = <
  TInputs extends Readonly<any[]>,
>(): OperatorFunction<TInputs, TInputs> =>
  distinctUntilChanged((input1, input2) =>
    input1.every((v, idx) => Object.is(v, input2[idx])),
  );
